import { createFileRoute, Link } from "@tanstack/react-router";
import { BarChart3, FileText, GraduationCap, LifeBuoy, Megaphone, Stethoscope } from "lucide-react";

import { seo } from "@/lib/seo";
import { getCaseStudies, getTestimonials } from "@/lib/content";
import {
  ServicePageLayout,
  serviceJsonLd,
  type ServicePageContent,
} from "@/components/service/ServicePageLayout";

const SERVICE_TAG = "ai-enablement";
const PATH = "/services/ai-enablement";
const NAME = "AI Enablement & Training";
const TITLE = "AI Training & Adoption for Small Business";
const SERVICE_TYPE = "AI training and adoption for small business teams";
const DESCRIPTION =
  "AI training and change management for small business teams, so the systems you invested in are still being used six months later.";

const FAQS = [
  {
    q: "What is AI enablement?",
    a: "AI enablement is the work that helps a team use an AI system safely and consistently after it has been built or selected. It includes role-specific training, policy, ownership, usage measurement, documentation, and changes to the surrounding workflow. It is not a one-hour tool demo.",
  },
  {
    q: "How do you train employees to use AI tools?",
    a: "We train around the task, not the tool menu. Each role gets examples from its own work, clear rules for what the tool can and cannot do, and practice reviewing outputs. The goal is for people to know when to use the system, when to check it, and when to ignore it.",
  },
  {
    q: "How do you get a team to actually adopt a new system?",
    a: "First we find out why they are not using it. Sometimes they were never trained. Sometimes the new workflow is slower than the old one. Sometimes a manager still asks for the old spreadsheet, so the old process wins. Adoption improves when ownership, incentives, training, and workflow design point in the same direction.",
  },
  {
    q: "Do you write an AI usage policy?",
    a: "Yes, when it is needed. A useful policy is short, specific, and tied to real work: what information may go into AI tools, what must stay out, which outputs need review, who approves new uses, and where employees go with questions. A policy nobody reads is not control.",
  },
  {
    q: "What if our staff are worried AI will replace them?",
    a: "We address that directly. People do not trust vague reassurance. They need to know what is changing, what is not changing, how performance will be judged, and where human judgment still matters. If leadership is actually pursuing job cuts, that has to be said plainly because it changes the work.",
  },
  {
    q: "We did not build it with you. Can you still help?",
    a: "Yes. A good share of enablement work is on systems somebody else delivered. We do not need to have built it to find out why it is not being used or why the team does not trust it.",
  },
  {
    q: "Is AI enablement just change management?",
    a: "It includes change management, but it is more specific. AI changes how people draft, decide, review, escalate, and explain work. Enablement has to cover the human workflow and the behaviour of the system itself.",
  },
  {
    q: "How do you measure AI adoption?",
    a: "We use actual usage data wherever the system exposes it, supported by conversations with the people using it. Self-reported adoption is unreliable in both directions. We look for use, correct use, avoidance, rework, and places where people still keep a shadow process.",
  },
  {
    q: "What if the real problem is the software?",
    a: "Then we say so. Training a team to tolerate a badly designed workflow is not a service we are willing to sell. The right next step may be a fix to the system, a smaller rollout, or retiring the tool.",
  },
  {
    q: "How long does AI training take?",
    a: "It depends on how many roles are affected and how much of the workflow changes. Some teams need a few focused sessions and better documentation. Others need a diagnosis, policy work, manager coaching, and follow-up after the system has been used in real conditions.",
  },
] as const;

const CONTENT: ServicePageContent = {
  path: PATH,
  name: NAME,
  eyebrow: "AI Enablement",
  h1: "AI training and adoption, so your team actually uses what you built.",
  lead: "Most AI investments don't fail technically. They fail in week three, when the people it was built for quietly go back to the way they did it before. Enablement is the work that stops that.",
  problem: {
    heading: "Adoption is where AI investments quietly die.",
    paragraphs: [
      "The failure is rarely dramatic. Nobody announces that they have stopped using the new tool. Usage just decays. One person reverts because it got something wrong. Another was never trained properly. A manager still asks for the old spreadsheet. Within a quarter the old process is back.",
      "By then the budget is spent, the project is nominally delivered, and nobody wants to reopen it. The system may work exactly as designed and still fail because the surrounding habits, policy, and ownership were never changed.",
      "AI enablement treats adoption as an operating problem. What does each role do differently? What decisions still belong to a person? What information is safe to use? Who owns the answer when the tool is wrong? If those questions are not answered, training alone will not hold.",
    ],
  },
  useCases: {
    heading: "What adoption work looks like by function.",
    intro:
      "Enablement is practical. It turns a new system into normal work, with clear owners, rules, and habits for the teams using it.",
    groups: [
      {
        area: "Customer communications",
        examples: [
          "A review step for AI-drafted customer messages before anything leaves the business.",
          "Approved examples showing tone, escalation rules, and when a person must write the reply.",
          "Manager checks for whether the team is using the new inbox workflow correctly.",
          "Clear boundaries for what customer information may be used in AI tools.",
        ],
      },
      {
        area: "Documents and data",
        examples: [
          "Training on how to check extracted fields rather than retyping the whole document.",
          "A policy for which documents can be uploaded to which tools.",
          "Ownership of the old spreadsheet by the person now responsible for the new process.",
          "Error examples that show staff what a bad output looks like.",
        ],
      },
      {
        area: "Sales and proposals",
        examples: [
          "Rules for using proposal drafts without sending unsupported claims to prospects.",
          "Coaching managers to review substance rather than editing every sentence back to the old style.",
          "A shared prompt and source library so each salesperson is not inventing their own method.",
          "Usage checks to see whether people still build proposals from old files.",
        ],
      },
      {
        area: "Scheduling",
        examples: [
          "Training staff on exception handling when the scheduling workflow cannot decide.",
          "A clear owner for calendar rules so the system does not drift out of date.",
          "Scripts for explaining appointment changes to customers when AI drafted the message.",
          "Manager review of missed or overridden suggestions during the first live period.",
        ],
      },
      {
        area: "Knowledge",
        examples: [
          "A maintained source library so the answer tool does not depend on stale documents.",
          "Guidance on asking better questions and checking cited sources.",
          "A feedback path when employees find an answer that is wrong or incomplete.",
          "Role-specific examples for new hires, supervisors, and frontline staff.",
        ],
      },
      {
        area: "Finance and admin",
        examples: [
          "A written policy for invoice, payroll, loan, grant, and tax-adjacent information.",
          "Review rules for AI-assisted summaries before they reach leadership or outside parties.",
          "Training on when automation output is a draft versus a record.",
          "Ownership of month-end checks so people do not rebuild the old manual report in parallel.",
        ],
      },
    ],
  },
  cards: [
    {
      icon: Stethoscope,
      title: "Adoption diagnosis",
      body: "Where usage is dropping off, and the actual reason rather than the assumed one.",
    },
    {
      icon: GraduationCap,
      title: "Role-specific training",
      body: "Built around what each role does daily, not a generic tool overview.",
    },
    {
      icon: FileText,
      title: "Documentation people will read",
      body: "Short, task-based, and kept where the work happens.",
    },
    {
      icon: Megaphone,
      title: "Internal champions",
      body: "Named people in each team with the context to help colleagues.",
    },
    {
      icon: BarChart3,
      title: "Usage measurement",
      body: "Actual usage data, so decay is visible while it is still fixable.",
    },
    {
      icon: LifeBuoy,
      title: "Follow-through",
      body: "Scheduled check-ins after go-live, when the real problems surface.",
    },
  ],
  deliverables: {
    heading: "What you actually receive.",
    intro: "Practical materials, not a change-management framework.",
    items: [
      "An adoption assessment with the specific blockers found",
      "Role-based training sessions, delivered",
      "Task-based documentation your team can maintain",
      "A usage dashboard or report showing real adoption over time",
      "A follow-up review after live use begins",
    ],
  },
  readiness: {
    heading: "When enablement is the missing piece.",
    ready: [
      "You have built or bought an AI tool and usage is low, uneven, or hard to prove.",
      "People are keeping an old spreadsheet, inbox habit, or manual checklist alongside the new system.",
      "Managers agree the system should be used but have not changed the surrounding expectations.",
      "Staff need clear rules for privacy, review, escalation, and acceptable use.",
      "There is a named owner who can keep documentation and policy current.",
    ],
    notYet: [
      "Nothing exists for the team to adopt yet.",
      "The tool is still changing so often that training would be obsolete immediately.",
      "Leadership has not decided whether the new workflow is actually required.",
      "The system produces outputs nobody can safely review.",
      "The real issue is a broken build, not a training or adoption problem.",
    ],
  },
  process: {
    heading: "How it runs.",
    steps: [
      {
        n: "1",
        title: "Discovery",
        body: "We look at what was built, who it was for, and what is actually happening now.",
      },
      {
        n: "2",
        title: "Diagnose",
        body: "We talk to the people meant to be using it and find out why they are not.",
      },
      {
        n: "3",
        title: "Design the change",
        body: "What each role does differently, and what support they need to do it.",
      },
      {
        n: "4",
        title: "Train and document",
        body: "Delivered by role, in the context of real work.",
      },
      {
        n: "5",
        title: "Measure and follow up",
        body: "Usage tracked, a later review held, and adjustments made where the workflow is breaking down.",
      },
    ],
  },
  timeline: {
    heading: "How long it takes.",
    body: "Enablement can begin in days when the system and affected roles are clear. Broader adoption work takes a few weeks or more because it has to include diagnosis, policy, training, manager expectations, and follow-up after real use. The timing depends less on the AI tool and more on how many people must change their routine.",
  },
  pricing: {
    heading: "What it costs.",
    body: "Enablement is fixed-quote and scoped to the number of roles and teams involved. Where an adoption problem turns out to be a product problem, we will tell you — training cannot fix a system that does not work.",
  },
  related: {
    heading: "What this assumes, and what it pairs with.",
    body: (
      <>
        <p>
          Not sure this is the right starting point? The{" "}
          <Link to="/tools/ai-opportunity-scorecard">free AI readiness scorecard</Link> takes a few
          minutes, and if nothing has been built yet an{" "}
          <Link to="/services/ai-opportunity-assessment">AI opportunity assessment</Link> is the
          honest first step.
        </p>
        <p>
          Adoption work often surfaces steps that should not have been anyone&rsquo;s job in the
          first place. Those get handed to{" "}
          <Link to="/services/workflow-automation">AI workflow automation</Link> rather than trained
          around.
        </p>
      </>
    ),
  },
  faqs: FAQS,
  cta: {
    heading: "Built something nobody is using?",
    line: "A short call is usually enough to tell whether the problem is training, policy, ownership, or the system itself.",
  },
};

export const Route = createFileRoute("/_site/services/ai-enablement")({
  head: () =>
    seo({
      title: TITLE,
      description: DESCRIPTION,
      path: PATH,
      jsonLd: serviceJsonLd({
        path: PATH,
        name: NAME,
        description: DESCRIPTION,
        serviceType: SERVICE_TYPE,
        faqs: FAQS,
      }),
    }),
  loader: async () => {
    const [caseStudies, testimonials] = await Promise.all([
      getCaseStudies({ data: {} }),
      getTestimonials({ data: { limit: 3 } }),
    ]);
    return {
      caseStudies: caseStudies.filter((study) => study.services?.includes(SERVICE_TAG)).slice(0, 3),
      testimonials: testimonials.filter((t) => !t.service_tag || t.service_tag === SERVICE_TAG),
    };
  },
  component: Page,
  errorComponent: () => <ServicePageLayout content={CONTENT} />,
});

function Page() {
  const { caseStudies, testimonials } = Route.useLoaderData();
  return (
    <ServicePageLayout content={CONTENT} caseStudies={caseStudies} testimonials={testimonials} />
  );
}
